import { useState, useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectAgoraChatServerConnection,
  selectAgoraChatId,
  selectAgoraChatToken,
} from 'src/consumer/reducers/personalData/personalDataReducer'
import { makeAndStoreAgoraConnectionInRedux } from 'src/components/FloatingIntercom/FloatingIntercomButton'
import { 
  sortMessagesArray,
  replaceTriedSentMessageWithResultantMessage,
  isOnlineNow,
} from 'src/components/ChatUIComponents/helperFunctions'
import { getConversationWithThisUser } from 'src/consumer/services/featureServices'
import { isSameUserId } from 'src/components/FloatingIntercom/helperFunctions'

export const useChatManagement = (profileData, peerChatId) => {
  const dispatch = useDispatch()
  const agoraChatServerConnection = useSelector(selectAgoraChatServerConnection)
  const agoraChatId = useSelector(selectAgoraChatId)
  const agoraChatToken = useSelector(selectAgoraChatToken)
  
  const [peerOnline, setPeerOnline] = useState(false)
  const [chatsWithThisUser, setChatsWithThisUser] = useState([])
  const [scrollTriggerBoolean, setScrollTriggerBoolean] = useState(false)
  const [chatsLoading, setChatsLoading] = useState(false)
  const [page, setPage] = useState(1)

  const agoraAppKey = import.meta.env.VITE_AGORA_CHAT_APPKEY

  // Initialize Agora connection
  useEffect(() => {
    if (agoraChatServerConnection && agoraChatId && agoraChatToken) {
      console.log('Chat connection already exists')
    } else {
      makeAndStoreAgoraConnectionInRedux(dispatch, agoraAppKey, profileData?.id)
    }
  }, [agoraChatServerConnection, agoraChatId, agoraChatToken, profileData, dispatch, agoraAppKey])

  // Get initial chats with user
  const getChatsWithThisUser = useCallback(async () => {
    if (agoraChatServerConnection && peerChatId) {
      setChatsLoading(true)
      const conversationResponse = await getConversationWithThisUser(peerChatId, page)
      setChatsLoading(false)
      
      if (conversationResponse?.status === 200 && Array.isArray(conversationResponse?.data)) {
        setChatsWithThisUser(sortMessagesArray(conversationResponse?.data))
      } else {
        setChatsWithThisUser([])
      }
    }
  }, [agoraChatServerConnection, peerChatId, page])

  useEffect(() => {
    getChatsWithThisUser()
  }, [getChatsWithThisUser])

  // Load more chats (pagination)
  const loadMoreChats = useCallback(async (userId) => {
    if (Array.isArray(chatsWithThisUser)) {
      setChatsLoading(true)
      const conversationResponse = await getConversationWithThisUser(userId, page + 1)
      setChatsLoading(false)
      
      if (
        conversationResponse?.status === 200 &&
        Array.isArray(conversationResponse?.data) &&
        conversationResponse?.data?.length > 0
      ) {
        setChatsWithThisUser((prev) => sortMessagesArray([...conversationResponse?.data, ...prev]))
        setPage((prev) => prev + 1)
      } else {
        console.log('no array found, some problem')
      }
    }
  }, [chatsWithThisUser, page])

  // Replace waiting temporary message
  const replaceWaitingTempMessage = useCallback((userId, tempMsgIdToReplace, resultMessage) => {
    setChatsWithThisUser((prev) => {
      const newMessagesArray = replaceTriedSentMessageWithResultantMessage(
        prev || [],
        tempMsgIdToReplace,
        resultMessage,
      )
      return newMessagesArray
    })
  }, [])

  // Add local message to array
  const addLocalMessageToArray = useCallback((data) => {
    setChatsWithThisUser((prev) => [...prev, data])
    setScrollTriggerBoolean((prev) => !prev)
  }, [])

  // Listen to incoming messages
  useEffect(() => {
    if (agoraChatServerConnection && agoraChatId && agoraChatToken && peerChatId) {
      agoraChatServerConnection?.addEventHandler?.('message', {
        onTextMessage: function (message) {
          if (isSameUserId(message?.to, agoraChatId) && isSameUserId(message?.from, peerChatId)) {
            setChatsWithThisUser((prev) => [...prev, message])
            setScrollTriggerBoolean((prev) => !prev)
          }
        },
        onCustomMessage: function (message) {
          if (isSameUserId(message?.to, agoraChatId) && isSameUserId(message?.from, peerChatId)) {
            setChatsWithThisUser((prev) => [...prev, message])
            setScrollTriggerBoolean((prev) => !prev)
          }
        },
      })
    }
    
    return () => {
      if (agoraChatServerConnection) {
        agoraChatServerConnection?.removeEventHandler?.('message')
      }
    }
  }, [agoraChatServerConnection, agoraChatId, agoraChatToken, peerChatId])

  // Check presence of peer
  const keepCheckingPresenceOfOther = useCallback(() => {
    if (agoraChatServerConnection && peerChatId) {
      let option = {
        usernames: [peerChatId?.toLowerCase()],
      }
      agoraChatServerConnection.getPresenceStatus(option).then((res) => {
        const presenceDataOfPeer = res?.data?.result?.find((resultItem) =>
          isSameUserId(resultItem?.uid, peerChatId),
        )
        setPeerOnline(
          presenceDataOfPeer?.ext ? isOnlineNow(presenceDataOfPeer.ext?.slice(7)) : false,
        )
      })
    }
  }, [agoraChatServerConnection, peerChatId])

  useEffect(() => {
    keepCheckingPresenceOfOther()
    const presenceInterval = setInterval(() => {
      keepCheckingPresenceOfOther()
    }, 30000) // 30 seconds
    
    return () => {
      clearInterval(presenceInterval)
    }
  }, [keepCheckingPresenceOfOther])

  return {
    // Connection state
    agoraChatServerConnection,
    agoraChatId,
    agoraChatToken,
    
    // Chat state
    peerOnline,
    chatsWithThisUser,
    scrollTriggerBoolean,
    chatsLoading,
    page,
    
    // Methods
    loadMoreChats,
    replaceWaitingTempMessage,
    addLocalMessageToArray,
  }
}